import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import {
  Clock, CheckCircle2, XCircle, ChevronLeft, ChevronRight,
  Loader2, ArrowLeft, Trophy, BarChart3, Play, BookOpen,
  Timer, Target, AlertTriangle, Sparkles, Upload, Plus, 
  Pencil, Trash2, FileText, X, Save, Brain, ChevronDown, Eye, Swords
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import PageSkeleton from '../components/PageSkeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GRADES = {
  A: { color: '#22c55e', label: 'Excellent' },
  B: { color: '#3b82f6', label: 'Good' },
  C: { color: '#eab308', label: 'Average' },
  D: { color: '#f97316', label: 'Below Average' },
  F: { color: '#ef4444', label: 'Needs Improvement' },
};

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const ExamModePage = () => {
  const [phase, setPhase] = useState('select'); // select | config | exam | results | bank | aiprep
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [examData, setExamData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQ, setCurrentQ] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [results, setResults] = useState(null);
  const [history, setHistory] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [questionBank, setQuestionBank] = useState([]);
  const [bankFilter, setBankFilter] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingQ, setEditingQ] = useState(null);
  const [importing, setImporting] = useState(false);
  const [newQ, setNewQ] = useState({ subject_id: '', question: '', options: ['', '', '', ''], correct: '', explanation: '' });
  // Exam config state
  const [examConfig, setExamConfig] = useState({ questionCount: 10, timeLimit: 15, difficulty: 'mixed' });
  // AI Prep state
  const [aiPrepForm, setAiPrepForm] = useState({ topic: '', exam_system: 'university', difficulty: 'medium', question_count: 5, subject: '' });
  const [aiPrepLoading, setAiPrepLoading] = useState(false);
  const [aiPrepQuestions, setAiPrepQuestions] = useState(null);
  const [revealedAnswers, setRevealedAnswers] = useState({});
  const startTimeRef = useRef(null);
  const timerRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [subRes, histRes] = await Promise.all([
        axios.get(`${API}/exam-mode/subjects`),
        axios.get(`${API}/exam-mode/history?limit=10`),
      ]);
      setSubjects(subRes.data.subjects || []);
      setHistory(histRes.data);
    } catch (err) {
      console.error('Error fetching exam data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchQuestionBank = async () => {
    try {
      const res = await axios.get(`${API}/exam-mode/question-bank${bankFilter ? `?subject_id=${bankFilter}` : ''}`);
      setQuestionBank(res.data.questions || []);
    } catch (err) {
      console.error('Error fetching question bank:', err);
    }
  };

  useEffect(() => {
    if (phase === 'bank') fetchQuestionBank();
  }, [phase, bankFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const subjectId = bankFilter || subjects[0]?.subject_id;
    if (!subjectId) { toast.error('Select a subject first'); return; }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('subject_id', subjectId);
      const res = await axios.post(`${API}/exam-mode/import`, formData);
      toast.success(res.data.message);
      fetchQuestionBank();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleAddQuestion = async () => {
    if (!newQ.subject_id || !newQ.question || !newQ.correct) { toast.error('Fill all required fields'); return; }
    const opts = newQ.options.filter(o => o.trim());
    if (opts.length < 2) { toast.error('At least 2 options required'); return; }
    try {
      await axios.post(`${API}/exam-mode/questions`, { ...newQ, options: opts });
      toast.success('Question added!');
      setShowAddForm(false);
      setNewQ({ subject_id: '', question: '', options: ['', '', '', ''], correct: '', explanation: '' });
      fetchQuestionBank();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add question');
    }
  };

  const handleEditQuestion = async (id) => {
    if (!editingQ) return;
    try {
      await axios.put(`${API}/exam-mode/questions/${id}`, editingQ);
      toast.success('Question updated!');
      setEditingQ(null);
      fetchQuestionBank();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDeleteQuestion = async (id) => {
    try {
      await axios.delete(`${API}/exam-mode/questions/${id}`);
      toast.success('Question deleted');
      fetchQuestionBank();
    } catch (err) {
      toast.error('Failed to delete question');
    }
  };

  // Timer
  useEffect(() => {
    if (phase === 'exam' && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const startExam = async (subjectId) => {
    try {
      setLoading(true);
      const params = subjectId ? `subject_id=${subjectId}&` : '';
      const res = await axios.post(
        `${API}/exam-mode/start?${params}question_count=${examConfig.questionCount}&time_limit_minutes=${examConfig.timeLimit}`
      );
      setExamData(res.data);
      setAnswers({});
      setCurrentQ(0);
      setTimeLeft(res.data.time_limit_minutes * 60);
      startTimeRef.current = Date.now();
      setPhase('exam');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start exam');
    } finally {
      setLoading(false);
    }
  };

  const openExamConfig = (subjectId) => {
    setSelectedSubject(subjectId);
    setPhase('config');
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    clearInterval(timerRef.current);
    const timeTaken = Math.round((Date.now() - startTimeRef.current) / 1000);
    try {
      const res = await axios.post(`${API}/exam-mode/submit`, {
        exam_id: examData.exam_id,
        answers,
        time_taken_seconds: timeTaken,
      });
      setResults(res.data);
      setPhase('results');
    } catch (err) {
      toast.error('Failed to submit exam');
    } finally {
      setSubmitting(false);
    }
  };

  const selectAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const resetExam = () => {
    setPhase('select');
    setExamData(null);
    setResults(null);
    setAnswers({});
    setCurrentQ(0);
    fetchData();
  };

  const handleAiPrepGenerate = async () => {
    if (!aiPrepForm.topic.trim()) { toast.error('Enter a topic'); return; }
    setAiPrepLoading(true);
    setAiPrepQuestions(null);
    setRevealedAnswers({});
    try {
      const res = await axios.post(`${API}/ai/hybrid/exam-prep`, aiPrepForm);
      setAiPrepQuestions(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate questions');
    } finally {
      setAiPrepLoading(false);
    }
  };

  // === EXAM CONFIG PHASE ===
  if (phase === 'config') {
    const selectedSubjectInfo = subjects.find(s => s.subject_id === selectedSubject);
    const maxQuestions = selectedSubject
      ? (selectedSubjectInfo?.question_count || 10)
      : subjects.reduce((sum, s) => sum + s.question_count, 0);

    return (
      <div className="max-w-lg mx-auto space-y-6" data-testid="exam-config">
        <div className="flex items-center gap-3">
          <Button onClick={() => setPhase('select')} variant="outline" size="sm" className="rounded-xl border-[var(--isp-glass-border)] text-[var(--isp-body)]" data-testid="config-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[var(--isp-heading)]">Configure Exam</h1>
            <p className="text-sm text-[var(--isp-body)]">{selectedSubject ? selectedSubjectInfo?.subject_name : 'All Subjects'}</p>
          </div>
        </div>

        <div className="p-6  isp-card space-y-6">
          {/* Question Count */}
          <div>
            <label className="flex items-center justify-between text-sm font-medium text-[var(--isp-heading)] mb-3">
              <span className="flex items-center gap-2"><BookOpen className="w-4 h-4 text-purple-400" /> Questions</span>
              <span className="text-lg font-bold text-purple-400">{examConfig.questionCount}</span>
            </label>
            <input
              type="range"
              min={3}
              max={Math.min(50, maxQuestions)}
              value={examConfig.questionCount}
              onChange={e => setExamConfig(prev => ({ ...prev, questionCount: parseInt(e.target.value) }))}
            className="w-full h-2 rounded-full appearance-none bg-white/10 accent-emerald-500"
              data-testid="config-question-count"
            />
            <div className="flex justify-between text-[10px] text-[var(--isp-muted)] mt-1">
              <span>3</span>
              <span>{Math.min(50, maxQuestions)}</span>
            </div>
          </div>

          {/* Time Limit */}
          <div>
            <label className="flex items-center justify-between text-sm font-medium text-[var(--isp-heading)] mb-3">
              <span className="flex items-center gap-2"><Timer className="w-4 h-4 text-blue-400" /> Time Limit</span>
              <span className="text-lg font-bold text-blue-400">{examConfig.timeLimit} min</span>
            </label>
            <input
              type="range"
              min={5}
              max={120}
              step={5}
              value={examConfig.timeLimit}
              onChange={e => setExamConfig(prev => ({ ...prev, timeLimit: parseInt(e.target.value) }))}
              className="w-full h-2 rounded-full appearance-none bg-white/10 accent-blue-500"
              data-testid="config-time-limit"
            />
            <div className="flex justify-between text-[10px] text-[var(--isp-muted)] mt-1">
              <span>5 min</span>
              <span>120 min</span>
            </div>
          </div>

          {/* Difficulty */}
          <div>
            <label className="text-sm font-medium text-[var(--isp-heading)] mb-3 flex items-center gap-2">
              <Target className="w-4 h-4 text-amber-400" /> Difficulty
            </label>
            <div className="grid grid-cols-3 gap-2 mt-3">
              {[
                { value: 'easy', label: 'Easy', desc: 'Questions you know well', color: 'green' },
                { value: 'mixed', label: 'Mixed', desc: 'Balanced mix', color: 'blue' },
                { value: 'hard', label: 'Hard', desc: 'Focus on weak areas', color: 'red' },
              ].map(d => (
                <button
                  key={d.value}
                  onClick={() => setExamConfig(prev => ({ ...prev, difficulty: d.value }))}
                  className={`p-3 rounded-xl border text-center transition-all ${
                    examConfig.difficulty === d.value
                      ? `border-${d.color}-500/50 bg-${d.color}-500/15`
                      : 'border-[var(--isp-glass-border)] bg-[var(--isp-glass-inner)] hover:border-white/20'
                  }`}
                  data-testid={`config-difficulty-${d.value}`}
                >
                  <p className={`text-sm font-semibold ${
                    examConfig.difficulty === d.value
                      ? d.color === 'green' ? 'text-green-300' : d.color === 'blue' ? 'text-blue-300' : 'text-red-300'
                      : 'text-[var(--isp-body)]'
                  }`}>{d.label}</p>
                  <p className="text-[10px] text-[var(--isp-muted)] mt-0.5">{d.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <Button
            onClick={() => startExam(selectedSubject)}
            disabled={loading}
            className="w-full rounded-xl gap-2 isp-btn-primary font-semibold h-12 text-base"
            data-testid="start-exam-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />}
            Start Exam
          </Button>
        </div>
      </div>
    );
  }

  // === AI PREP PHASE ===
  if (phase === 'aiprep') {
    const examSystems = [
      { value: 'wassce', label: 'WASSCE', desc: 'West African SSC' },
      { value: 'waec', label: 'WAEC', desc: 'West African Exams Council' },
      { value: 'university', label: 'University', desc: 'Undergraduate level' },
      { value: 'general', label: 'General', desc: 'General academic' },
    ];
    const difficulties = ['easy', 'medium', 'hard'];

    return (
      <div className="space-y-6" data-testid="ai-exam-prep">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[var(--isp-heading)] tracking-tight flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" />
              </div>
              AI Exam Prep
            </h1>
            <p className="text-[var(--isp-body)] mt-1">Generate practice questions with AI for any topic</p>
          </div>
          <Button onClick={() => { setPhase('select'); setAiPrepQuestions(null); }} variant="outline" className="rounded-xl gap-2 border-[var(--isp-glass-border)] text-[var(--isp-body)]" data-testid="back-from-aiprep">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
        </div>

        {/* Config Card */}
        <div className="p-6 isp-card">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-[var(--isp-body)] mb-1.5">Topic *</label>
              <input
                value={aiPrepForm.topic}
                onChange={e => setAiPrepForm(p => ({ ...p, topic: e.target.value }))}
                placeholder="e.g., Photosynthesis, Calculus Integration, SQL Joins"
                className="w-full px-3 py-2.5 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-[var(--isp-heading)] text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD700]/50"
                data-testid="aiprep-topic"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--isp-body)] mb-1.5">Subject (optional)</label>
              <input
                value={aiPrepForm.subject}
                onChange={e => setAiPrepForm(p => ({ ...p, subject: e.target.value }))}
                placeholder="e.g., Biology, Mathematics"
                className="w-full px-3 py-2.5 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-[var(--isp-heading)] text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD700]/50"
                data-testid="aiprep-subject"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--isp-body)] mb-1.5">Questions</label>
              <input
                type="number" min={1} max={15}
                value={aiPrepForm.question_count}
                onChange={e => setAiPrepForm(p => ({ ...p, question_count: parseInt(e.target.value) || 5 }))}
                className="w-full px-3 py-2.5 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-[var(--isp-heading)] text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD700]/50"
                data-testid="aiprep-count"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--isp-body)] mb-1.5">Exam System</label>
              <div className="flex gap-2 flex-wrap">
                {examSystems.map(es => (
                  <button
                    key={es.value}
                    onClick={() => setAiPrepForm(p => ({ ...p, exam_system: es.value }))}
                    className={`px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                      aiPrepForm.exam_system === es.value
                        ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                        : 'bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)]'
                    }`}
                    data-testid={`aiprep-system-${es.value}`}
                  >
                    {es.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--isp-body)] mb-1.5">Difficulty</label>
              <div className="flex gap-2">
                {difficulties.map(d => (
                  <button
                    key={d}
                    onClick={() => setAiPrepForm(p => ({ ...p, difficulty: d }))}
                    className={`px-4 py-2 rounded-xl text-xs font-medium capitalize transition-all ${
                      aiPrepForm.difficulty === d
                        ? d === 'easy' ? 'bg-green-500/20 text-green-300 border border-green-500/40'
                        : d === 'medium' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/40'
                        : 'bg-red-500/20 text-red-300 border border-red-500/40'
                        : 'bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)]'
                    }`}
                    data-testid={`aiprep-diff-${d}`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <Button
            onClick={handleAiPrepGenerate}
            disabled={aiPrepLoading || !aiPrepForm.topic.trim()}
            className="mt-5 w-full rounded-xl gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold"
            data-testid="aiprep-generate-btn"
          >
            {aiPrepLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
            {aiPrepLoading ? 'Generating...' : 'Generate Questions'}
          </Button>
        </div>

        {/* AI Generated Questions */}
        <AnimatePresence>
          {aiPrepQuestions && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="p-6 isp-card" data-testid="aiprep-results">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-lg font-semibold text-[var(--isp-heading)] flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-400" />
                    {aiPrepQuestions.questions?.length || 0} Questions — {aiPrepQuestions.topic}
                  </h2>
                  <span className="text-xs px-2 py-1 rounded-lg bg-amber-500/20 text-amber-300">{aiPrepQuestions.exam_system?.toUpperCase()}</span>
                </div>
                <div className="space-y-4">
                  {(aiPrepQuestions.questions || []).map((q, i) => (
                    <div key={`item-${i}`} className="p-4 rounded-xl border border-[var(--isp-glass-border)] bg-slate-800/30" data-testid={`aiprep-q-${i}`}>
                      <p className="text-sm font-medium text-white mb-3">
                        <span className="text-amber-400 mr-2">Q{i + 1}.</span>
                        {q.question}
                      </p>
                      {q.type === 'mcq' && q.options && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                          {(Array.isArray(q.options) ? q.options : Object.entries(q.options || {})).map((opt, oi) => {
                            const label = Array.isArray(q.options) ? String.fromCharCode(65 + oi) : opt[0];
                            const text = Array.isArray(q.options) ? opt : opt[1];
                            const isCorrect = revealedAnswers[i] && (q.correct_answer === label || q.correct_answer === text);
                            return (
                              <div
                                key={oi}
                                className={`px-3 py-2 rounded-lg text-xs border ${
                                  isCorrect
                                    ? 'border-green-500/50 bg-green-500/15 text-green-300'
                                    : 'border-[var(--isp-glass-border)] text-[var(--isp-body)]'
                                }`}
                              >
                                <span className="font-semibold mr-1.5">{label}.</span> {text}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setRevealedAnswers(p => ({ ...p, [i]: !p[i] }))}
                          className="rounded-lg gap-1.5 text-xs border-[var(--isp-glass-border)] text-[var(--isp-body)]"
                          data-testid={`reveal-${i}`}
                        >
                          <Eye className="w-3 h-3" /> {revealedAnswers[i] ? 'Hide' : 'Reveal'} Answer
                        </Button>
                        {q.concept_tested && (
                          <span className="text-[10px] px-2 py-1 rounded-md bg-purple-500/15 text-purple-300">{q.concept_tested}</span>
                        )}
                      </div>
                      <AnimatePresence>
                        {revealedAnswers[i] && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                            <div className="mt-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                              <p className="text-xs text-emerald-300 font-semibold mb-1">Answer: {q.correct_answer}</p>
                              {q.explanation && <p className="text-xs text-[var(--isp-body)]">{q.explanation}</p>}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // === QUESTION BANK PHASE ===
  if (phase === 'bank') {
    const allSubjects = [...new Map(questionBank.map(q => [q.subject_id, { id: q.subject_id, name: q.subject_name, color: q.subject_color }])).values()];

    return (
      <div className="space-y-6" data-testid="question-bank">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[var(--isp-heading)] tracking-tight flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              Question Bank
            </h1>
            <p className="text-[var(--isp-body)] mt-1">{questionBank.length} questions</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setPhase('select')} variant="outline" className="rounded-xl gap-2 border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]" data-testid="back-to-exams-btn">
              <ArrowLeft className="w-4 h-4" /> Back to Exams
            </Button>
            <Button onClick={() => setShowAddForm(true)} className="rounded-xl gap-2 bg-gradient-to-r from-green-500 to-emerald-500" data-testid="add-question-btn">
              <Plus className="w-4 h-4" /> Add Question
            </Button>
            <label className="cursor-pointer">
              <input type="file" accept=".pdf,.txt,.text" onChange={handleImport} className="hidden" data-testid="import-file-input" />
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-medium hover:from-amber-600 hover:to-orange-600">
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Import Paper
              </div>
            </label>
          </div>
        </div>

        {/* Subject filter */}
        {allSubjects.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setBankFilter('')} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${!bankFilter ? 'bg-[var(--isp-accent)]/20 text-[var(--isp-accent)] border border-[var(--isp-accent)]/30' : 'text-[var(--isp-body)] hover:text-[var(--isp-heading)]'}`}>All</button>
            {subjects.map(s => (
              <button key={s.subject_id} onClick={() => setBankFilter(s.subject_id)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${bankFilter === s.subject_id ? 'bg-[var(--isp-accent)]/20 text-[var(--isp-accent)] border border-[var(--isp-accent)]/30' : 'text-[var(--isp-body)] hover:text-[var(--isp-heading)]'}`}>
                {s.subject_name}
              </button>
            ))}
          </div>
        )}

        {/* Add Question Form */}
        <AnimatePresence>
          {showAddForm && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
              <div className="p-6 isp-card" data-testid="add-question-form">
                <h3 className="text-lg font-semibold text-[var(--isp-heading)] mb-4">Add New Question</h3>
                <div className="space-y-4">
                  <select value={newQ.subject_id} onChange={e => setNewQ({ ...newQ, subject_id: e.target.value })} className="w-full p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-white text-sm" data-testid="add-q-subject">
                    <option value="">Select Subject</option>
                    {subjects.map(s => <option key={s.subject_id} value={s.subject_id}>{s.subject_name}</option>)}
                  </select>
                  <input placeholder="Question text" value={newQ.question} onChange={e => setNewQ({ ...newQ, question: e.target.value })} className="w-full p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-white text-sm" data-testid="add-q-text" />
                  {[0, 1, 2, 3].map(i => (
                    <input key={`item-${i}`} placeholder={`Option ${i + 1}`} value={newQ.options[i]} onChange={e => { const opts = [...newQ.options]; opts[i] = e.target.value; setNewQ({ ...newQ, options: opts }); }} className="w-full p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-white text-sm" data-testid={`add-q-option-${i}`} />
                  ))}
                  <select value={newQ.correct} onChange={e => setNewQ({ ...newQ, correct: e.target.value })} className="w-full p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-white text-sm" data-testid="add-q-correct">
                    <option value="">Select Correct Answer</option>
                    {newQ.options.filter(o => o.trim()).map((o, i) => <option key={`item-${i}`} value={o}>{o}</option>)}
                  </select>
                  <input placeholder="Explanation (optional)" value={newQ.explanation} onChange={e => setNewQ({ ...newQ, explanation: e.target.value })} className="w-full p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-white text-sm" data-testid="add-q-explanation" />
                  <div className="flex gap-2">
                    <Button onClick={handleAddQuestion} className="rounded-xl gap-2 bg-gradient-to-r from-green-500 to-emerald-500" data-testid="save-question-btn">
                      <Save className="w-4 h-4" /> Save
                    </Button>
                    <Button onClick={() => setShowAddForm(false)} variant="outline" className="rounded-xl border-[var(--isp-glass-border)] text-[var(--isp-body)]">Cancel</Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Question List */}
        {questionBank.length === 0 ? (
          <div className="p-12 isp-card text-center">
            <FileText className="w-12 h-12 mx-auto mb-3 text-[var(--isp-muted)]" />
            <p className="text-[var(--isp-body)]">No questions in your bank yet</p>
            <p className="text-sm text-[var(--isp-muted)] mt-1">Add manually or import from a past exam paper</p>
          </div>
        ) : (
          <div className="space-y-3">
            {questionBank.map((q, i) => (
              <div key={q.id} className="p-4 isp-card" data-testid={`bank-question-${i}`}>
                {editingQ?.id === q.id ? (
                  <div className="space-y-3">
                    <input value={editingQ.question} onChange={e => setEditingQ({ ...editingQ, question: e.target.value })} className="w-full p-2 rounded-lg bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-white text-sm" />
                    {(editingQ.options || []).map((o, oi) => (
                      <input key={oi} value={o} onChange={e => { const opts = [...editingQ.options]; opts[oi] = e.target.value; setEditingQ({ ...editingQ, options: opts }); }} className="w-full p-2 rounded-lg bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-white text-sm" />
                    ))}
                    <select value={editingQ.correct} onChange={e => setEditingQ({ ...editingQ, correct: e.target.value })} className="w-full p-2 rounded-lg bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-white text-sm">
                      {(editingQ.options || []).filter(o => o.trim()).map((o, oi) => <option key={oi} value={o}>{o}</option>)}
                    </select>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleEditQuestion(q.id)} className="rounded-lg gap-1 bg-green-500/80 hover:bg-green-600"><Save className="w-3 h-3" /> Save</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingQ(null)} className="rounded-lg border-[var(--isp-glass-border)] text-[var(--isp-body)]">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: q.subject_color }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">{q.question}</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {q.options.map((o, oi) => (
                          <span key={oi} className={`px-2 py-0.5 rounded-md text-xs ${o === q.correct ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'bg-[var(--isp-glass-inner)] text-[var(--isp-body)]'}`}>{o}</span>
                        ))}
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-xs text-[var(--isp-muted)]">
                        <span>{q.subject_name}</span>
                        <span>{q.source === 'imported' ? 'Imported' : q.source === 'manual' ? 'Manual' : 'AI Generated'}</span>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => setEditingQ({ id: q.id, question: q.question, options: q.options, correct: q.correct, explanation: q.explanation })} className="h-8 w-8 p-0 text-[var(--isp-muted)] hover:text-[var(--isp-heading)]" data-testid={`edit-q-${i}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteQuestion(q.id)} className="h-8 w-8 p-0 text-[var(--isp-muted)] hover:text-red-400" data-testid={`delete-q-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (loading && phase === 'select') {
    return (
      <div className="space-y-6" data-testid="exam-loading">
        <PageSkeleton variant="grid" rows={4} />
      </div>
    );
  }

  // === RESULTS PHASE ===
  if (phase === 'results' && results) {
    const gradeInfo = GRADES[results.grade] || GRADES.F;
    return (
      <div className="space-y-6" data-testid="exam-results">
        <div className="text-center space-y-4">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
            className="w-24 h-24 mx-auto rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${gradeInfo.color}20`, border: `3px solid ${gradeInfo.color}` }}
          >
            <span className="text-4xl font-black" style={{ color: gradeInfo.color }}>{results.grade}</span>
          </motion.div>
          <h2 className="text-3xl font-bold text-white">{results.message}</h2>
          <div className="flex justify-center gap-8">
            <div className="text-center">
              <p className="text-3xl font-bold text-white">{results.score}%</p>
              <p className="text-xs text-[var(--isp-body)]">Score</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-green-400">{results.correct}</p>
              <p className="text-xs text-[var(--isp-body)]">Correct</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-red-400">{results.total - results.correct}</p>
              <p className="text-xs text-[var(--isp-body)]">Missed</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-400">{formatTime(results.time_taken_seconds)}</p>
              <p className="text-xs text-[var(--isp-body)]">Time</p>
            </div>
          </div>
        </div>

        {/* Answer Review */}
        <div className="p-6 isp-card">
          <h3 className="text-lg font-semibold text-[var(--isp-heading)] mb-4">Answer Review</h3>
          <div className="space-y-4">
            {results.results.map((r, i) => (
              <div key={`item-${i}`} className={`p-4 rounded-xl border ${r.is_correct ? 'bg-green-500/10 border-green-500/20' : 'bg-red-500/10 border-red-500/20'}`} data-testid={`result-${i}`}>
                <div className="flex items-start gap-3">
                  {r.is_correct ? (
                    <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">{r.question}</p>
                    {!r.is_correct && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-red-300">Your answer: {r.your_answer || 'Not answered'}</p>
                        <p className="text-xs text-green-300">Correct: {r.correct_answer}</p>
                      </div>
                    )}
                    {r.explanation && (
                      <p className="text-xs text-[var(--isp-body)] mt-2">{r.explanation}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-3 justify-center flex-wrap">
          <Button
            onClick={async () => {
              try {
                const res = await axios.post(`${API}/challenges/create`, {
                  exam_id: results.exam_id,
                  message: `I scored ${results.score}% on ${examData?.subject_name || 'this quiz'} — think you can beat me?`,
                });
                const url = `${window.location.origin}/challenge/${res.data.challenge_id}`;
                await navigator.clipboard.writeText(url);
                toast.success('Challenge link copied! Share it with friends.');
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Failed to create challenge');
              }
            }}
            className="rounded-xl gap-2 bg-[#FF3B30]"
            data-testid="challenge-friend-btn"
          >
            <Swords className="w-4 h-4" /> Challenge a Friend
          </Button>
          <Button onClick={resetExam} className="rounded-xl gap-2 isp-btn-primary" data-testid="retake-exam-btn">
            <Play className="w-4 h-4" /> Take Another Exam
          </Button>
          <Link to="/app">
            <Button variant="outline" className="rounded-xl gap-2 border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]">
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // === EXAM PHASE ===
  if (phase === 'exam' && examData) {
    const question = examData.questions[currentQ];
    const answered = Object.keys(answers).length;
    const isTimeLow = timeLeft < 60;

    return (
      <div className="max-w-3xl mx-auto space-y-6" data-testid="exam-active">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">{examData.subject_name}</h2>
            <p className="text-sm text-[var(--isp-body)]">{answered}/{examData.question_count} answered</p>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${isTimeLow ? 'bg-red-500/20 border border-red-500/30' : 'bg-[var(--isp-glass)] backdrop-blur-xl border border-[var(--isp-glass-border)]'}`} data-testid="exam-timer">
            <Timer className={`w-4 h-4 ${isTimeLow ? 'text-red-400 animate-pulse' : 'text-[var(--isp-body)]'}`} />
            <span className={`font-mono text-lg ${isTimeLow ? 'text-red-400' : 'text-white'}`}>{formatTime(timeLeft)}</span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-2 bg-[var(--isp-glass-inner)] rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-[var(--isp-accent)] to-emerald-500 rounded-full"
            animate={{ width: `${((currentQ + 1) / examData.question_count) * 100}%` }}
          />
        </div>

        {/* Question */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQ}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.2 }}
          >
            <div className="p-6 sm:p-8 isp-card">
              <p className="text-xs text-[var(--isp-muted)] mb-3 font-mono">Question {currentQ + 1} of {examData.question_count}</p>
              <h3 className="text-lg sm:text-xl font-semibold text-[var(--isp-heading)] mb-6">{question.question}</h3>
              <div className="space-y-3">
                {question.options.map((option, i) => {
                  const isSelected = answers[question.id] === option;
                  return (
                    <button
                      key={`item-${i}`}
                      onClick={() => selectAnswer(question.id, option)}
                      className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
                        isSelected
                          ? 'bg-purple-500/20 border-purple-500/50 text-white'
                          : 'bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:bg-[var(--isp-glass-inner)] hover:border-white/20'
                      }`}
                      data-testid={`option-${i}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'border-[var(--isp-accent)] bg-[var(--isp-accent)]' : 'border-white/30'
                        }`}>
                          {isSelected && <div className="w-2 h-2 rounded-full bg-[var(--isp-page)]" />}
                        </div>
                        <span className="text-sm">{option}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentQ(Math.max(0, currentQ - 1))}
            disabled={currentQ === 0}
            className="rounded-xl gap-2 border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]"
            data-testid="prev-question-btn"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </Button>

          {/* Question dots */}
          <div className="flex gap-1.5 flex-wrap justify-center max-w-[200px]">
            {examData.questions.map((q, i) => (
              <button
                key={`item-${i}`}
                onClick={() => setCurrentQ(i)}
                className={`w-3 h-3 rounded-full transition-all ${
                  i === currentQ ? 'bg-[var(--isp-accent)] scale-125' :
                  answers[q.id] ? 'bg-green-500/70' : 'bg-slate-600'
                }`}
                data-testid={`dot-${i}`}
              />
            ))}
          </div>

          {currentQ < examData.question_count - 1 ? (
            <Button
              onClick={() => setCurrentQ(currentQ + 1)}
              className="rounded-xl gap-2 isp-btn-primary"
              data-testid="next-question-btn"
            >
              Next <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="rounded-xl gap-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
              data-testid="submit-exam-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Submit Exam
            </Button>
          )}
        </div>
      </div>
    );
  }

  // === SELECT PHASE ===
  return (
    <div className="space-y-6 sm:space-y-8" data-testid="exam-select">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[var(--isp-heading)] tracking-tight flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
              <Target className="w-6 h-6 text-white" />
            </div>
            Exam Mode
          </h1>
          <p className="text-[var(--isp-body)] mt-2 text-base sm:text-lg">
            Timed practice exams to test your knowledge
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/exam-analytics">
            <Button
              className="rounded-xl gap-2 bg-gradient-to-r from-cyan-500 to-teal-500 text-white"
              data-testid="open-analytics-btn"
            >
              <BarChart3 className="w-4 h-4" />
              Analytics
            </Button>
          </Link>
          <Button
            onClick={() => setPhase('aiprep')}
            className="rounded-xl gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white"
            data-testid="open-ai-prep-btn"
          >
            <Brain className="w-4 h-4" />
            AI Prep
          </Button>
          <Button
            onClick={() => setPhase('bank')}
            variant="outline"
            className="rounded-xl gap-2 border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]"
            data-testid="open-question-bank-btn"
          >
            <FileText className="w-4 h-4" />
            Question Bank
          </Button>
        </div>
      </div>

      {/* Quick stats */}
      {history && history.total_exams > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="p-4 isp-card" data-testid="stat-exams-taken">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500">
                <BookOpen className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{history.total_exams}</p>
                <p className="text-xs text-[var(--isp-body)]">Exams Taken</p>
              </div>
            </div>
          </div>
          <div className="p-4 isp-card" data-testid="stat-avg-score">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500">
                <BarChart3 className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{history.average_score}%</p>
                <p className="text-xs text-[var(--isp-body)]">Avg Score</p>
              </div>
            </div>
          </div>
          <div className="p-4 isp-card col-span-2 sm:col-span-1">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500">
                <Trophy className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {history.exams.length > 0 ? Math.max(...history.exams.map(e => e.score || 0)) : 0}%
                </p>
                <p className="text-xs text-[var(--isp-body)]">Best Score</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subject selection */}
      <div className="p-6 isp-card">
        <h2 className="text-xl font-semibold text-[var(--isp-heading)] mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[var(--isp-accent)]" />
          Choose Subject
        </h2>
        {subjects.length === 0 ? (
          <div className="text-center py-8">
            <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-[var(--isp-muted)]" />
            <p className="text-[var(--isp-body)]">No quiz questions available yet</p>
            <p className="text-sm text-[var(--isp-muted)] mt-1">Generate learning bytes from your lectures first</p>
            <Link to="/lectures">
              <Button className="mt-4 rounded-xl gap-2 isp-btn-primary">
                Go to Lectures
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* All subjects option */}
            <button
              onClick={() => openExamConfig(null)}
              className="p-4 rounded-xl border border-[var(--isp-accent)]/30 bg-[var(--isp-accent)]/10 text-left hover:bg-[var(--isp-accent)]/20 transition-all"
              data-testid="start-exam-all"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-white">All Subjects</p>
                  <p className="text-xs text-[var(--isp-body)] mt-1">
                    {subjects.reduce((sum, s) => sum + s.question_count, 0)} questions
                  </p>
                </div>
                <Play className="w-5 h-5 text-[var(--isp-accent)]" />
              </div>
            </button>

            {subjects.map((subj) => (
              <button
                key={subj.subject_id}
                onClick={() => subj.can_exam && openExamConfig(subj.subject_id)}
                disabled={!subj.can_exam}
                className={`p-4 rounded-xl border text-left transition-all ${
                  subj.can_exam
                    ? 'border-[var(--isp-glass-border)] bg-[var(--isp-glass)] backdrop-blur-xl hover:bg-[var(--isp-glass-inner)] hover:border-white/20'
                    : 'border-[var(--isp-glass-border)] bg-slate-800/30 opacity-50 cursor-not-allowed'
                }`}
                data-testid={`start-exam-${subj.subject_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: subj.subject_color }} />
                    <div>
                      <p className="font-medium text-white">{subj.subject_name}</p>
                      <p className="text-xs text-[var(--isp-body)] mt-1">
                        {subj.question_count} question{subj.question_count !== 1 ? 's' : ''}
                        {!subj.can_exam && ' (need 3+)'}
                      </p>
                    </div>
                  </div>
                  {subj.can_exam && <Play className="w-4 h-4 text-[var(--isp-muted)]" />}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Recent exam history */}
      {history && history.exams.length > 0 && (
        <div className="p-6 isp-card" data-testid="exam-history">
          <h2 className="text-xl font-semibold text-[var(--isp-heading)] mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-400" />
            Recent Exams
          </h2>
          <div className="space-y-3">
            {history.exams.map((exam, i) => {
              const gradeInfo = GRADES[exam.grade] || GRADES.F;
              return (
                <div key={`item-${i}`} className="flex items-center justify-between p-3 rounded-xl bg-slate-800/30 border border-[var(--isp-glass-border)]">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm"
                      style={{ backgroundColor: `${gradeInfo.color}20`, color: gradeInfo.color }}
                    >
                      {exam.grade}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{exam.subject_name || 'Mixed'}</p>
                      <p className="text-xs text-[var(--isp-muted)]">
                        {exam.correct_count}/{exam.total_questions} correct
                        {exam.time_taken_seconds && ` | ${formatTime(exam.time_taken_seconds)}`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold" style={{ color: gradeInfo.color }}>{exam.score}%</p>
                    <p className="text-[10px] text-[var(--isp-muted)]">
                      {exam.completed_at ? new Date(exam.completed_at).toLocaleDateString() : ''}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamModePage;
